import { useEffect, useState } from "react";
import * as XLSX from "xlsx";

export type SpreadsheetPreviewData = {
  sheetName: string;
  headers: string[];
  rows: string[][];
};

const SPREADSHEET_PREVIEW_MAX_ROWS = 500;

export function useSpreadsheetPreview(enabled: boolean, url: string | null | undefined) {
  const [data, setData] = useState<SpreadsheetPreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    if (!enabled || !url?.trim()) {
      setData(null);
      setLoading(false);
      setError(false);
      setTruncated(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);
    setData(null);
    setTruncated(false);

    void fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch spreadsheet");
        return res.arrayBuffer();
      })
      .then((buffer) => {
        const workbook = XLSX.read(buffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        if (!sheetName) throw new Error("No sheets found");
        const sheet = workbook.Sheets[sheetName];
        const rawRows = XLSX.utils.sheet_to_json<(string | number | boolean | null)[]>(sheet, {
          header: 1,
          defval: "",
        });
        const normalized = rawRows.map((row) =>
          row.map((cell) => (cell === null || cell === undefined ? "" : String(cell)))
        );
        const [headerRow = [], ...bodyRows] = normalized;
        const isTruncated = bodyRows.length > SPREADSHEET_PREVIEW_MAX_ROWS;
        return {
          sheetName,
          headers: headerRow,
          rows: isTruncated ? bodyRows.slice(0, SPREADSHEET_PREVIEW_MAX_ROWS) : bodyRows,
          truncated: isTruncated,
        };
      })
      .then((parsed) => {
        if (!cancelled) {
          setData({
            sheetName: parsed.sheetName,
            headers: parsed.headers,
            rows: parsed.rows,
          });
          setTruncated(parsed.truncated);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, url]);

  return { data, loading, error, truncated };
}
